import { store } from '../index';
import React, { useState, useEffect, useRef } from "react";
import { connect, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import defaultimg from "../assets/images/default.png";
import { get_customer_list } from "../utils/customer_helper";
import { set_last_order } from "../store/actions/action";
import { Typography, Box } from "@material-ui/core";
import { BUTTON_COLOR, TOKEN_KEY } from "../utils/constant";
const FRAME_WIDTH = 814;
const getFrameFixed = () => {
  if (window.innerWidth >= 768) {
    const offset = `calc(50% - ${FRAME_WIDTH / 2}px)`;
    return { left: offset, right: offset, width: `${FRAME_WIDTH}px` };
  }
  return { left: "0px", right: "0px", width: "100%" };
};

const pulseKeyframes = `
  @keyframes pulseOrange {
    0% { box-shadow: 0 0 0 0 rgba(255, 165, 0, 0.7); }
    70% { box-shadow: 0 0 0 6px rgba(255, 165, 0, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 165, 0, 0); }
  }
  @keyframes pulseGreen {
    0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
    70% { box-shadow: 0 0 0 6px rgba(76, 175, 80, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
  }
`;

const ActiveOrderTray = (props) => {
  const sessionReducer = useSelector(state => state.sessionReducer) || {};
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);

  const { reducer, cartProps, set_last_order } = props;
  const { customer, last_order, currency_code } = reducer;
  const storeInfo = props.sessionReducer?.store_details;
  const storeInfoConfig = props.sessionReducer?.store_config;
  const associated_stores = props.sessionReducer?.associated_stores;
  const primaryColor = storeInfoConfig?.general_config?.primary_color;
  const addedItems = cartProps?.addedItems || [];

  const [frameFixed, setFrameFixed] = useState(getFrameFixed());
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const handleResize = () => setFrameFixed(getFrameFixed());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!customer?.cust_mobile) return;

    const fetchActiveOrders = async () => {
      if (document.hidden) return;
      try {
        const resp = await get_customer_list({
          cust_mobile: customer?.cust_mobile,
        });
        if (resp?.order_details) {
          set_last_order(resp);
        }
      } catch (error) {
        console.error("[ActiveOrderTray] Error polling:", error);
      }
    };

    fetchActiveOrders();
    const intervalId = setInterval(fetchActiveOrders, 30000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchActiveOrders();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [customer?.cust_mobile, set_last_order]);

  const visibleRoutes = ["/stores", "/menu", "/cart"];
  if (!visibleRoutes.includes(location.pathname)) {
    return null;
  }

  if (location.pathname === "/cart" && location.state?.fsrView === "orders") {
    return null;
  }

  // ActiveOrderTray is not applicable in FSR (Table Management / Dine-In) mode.
  // The DiningFooter handles order tracking for FSR flows.
  let isTableMgmt = sessionReducer.is_fsr === true;
  const businessDate = sessionReducer?.store_details?.business_date;
  if (isTableMgmt) {
    return null;
  }

  const isLoggedIn = customer?.cust_mobile && customer?.guest !== "Guest";
  if (!isLoggedIn) {
    return null;
  }

  const isMultiStore = storeInfoConfig?.web_ordering_setting?.is_multi_store_qr;
  const allowedStoreIds = new Set();
  if (isMultiStore && Array.isArray(associated_stores) && associated_stores.length > 0) {
    associated_stores.forEach(s => {
      if (s?.store_id) allowedStoreIds.add(String(s.store_id));
    });
  } else if (storeInfo?.store_id) {
    allowedStoreIds.add(String(storeInfo.store_id));
  }

  const isOrderFromToday = (order) => {
    const rawDate = order?.business_date || order?.date_time || order?.created_at || "";
    const dateStr = rawDate.replace(" ", "T");
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const orderDateOnly = dateStr.slice(0, 10);
    if (businessDate) {
      return orderDateOnly === businessDate.slice(0, 10);
    }
    const today = new Date();
    return (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    );
  };

  let activeOrders = last_order?.order_details?.filter(
    (order) => {
      const orderStoreId = String(order?.store_details?.store_id || order?.store_id || "");
      if (!allowedStoreIds.has(orderStoreId)) return false;

      const status = (order?.order_status || "").toUpperCase();
      const state = (order?.order_state || "").toUpperCase();

      // Only show PLACED + OPEN orders from today's business date
      if (status !== "OPEN" || state !== "PLACED") return false;
      if (!isOrderFromToday(order)) return false;

      return true;
    }
  ) || [];

  // Sort by date_time descending (latest first)
  activeOrders.sort((a, b) => new Date(b.date_time) - new Date(a.date_time));

  // Limit to the latest two orders
  activeOrders = activeOrders.slice(0, 2);

  if (activeOrders.length === 0) return null;

  const handleScroll = (e) => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const width = scrollRef.current.offsetWidth;
      const index = Math.round(scrollLeft / width);
      if (index !== activeIndex && index >= 0 && index < activeOrders.length) {
        setActiveIndex(index);
      }
    }
  };

  const isCartNotEmpty = addedItems.length > 0;

  // On /cart the PaymentBtn can be ~130px tall (Note to Kitchen row + Place Order btn + padding).
  // On /menu with items the ViewBtn is ~80px.
  // Note: FSR mode returns null early above, so no DiningFooter offset is needed here.
  const baseOffset = location.pathname === "/cart"
    ? 140   // clears full PaymentBtn (Note to Kitchen + button + padding)
    : (location.pathname === "/menu" && isCartNotEmpty)
      ? 80
      : 16;
  const bottomOffset = `${baseOffset}px`;

  return (
    <>
      <style>{pulseKeyframes}</style>
      <div
        style={{
          position: "fixed",
          bottom: bottomOffset,
          ...frameFixed,
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pointerEvents: "none",
          transition: "bottom 0.3s ease",
        }}
      >
        <div
          style={{
            width: "calc(100% - 32px)",
            maxWidth: "800px",
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(255, 255, 255, 0.6)",
            pointerEvents: "auto",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {activeOrders.length > 1 && (
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px", gap: "6px" }}>
              {activeOrders.map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    width: activeIndex === idx ? "16px" : "6px",
                    height: "6px",
                    borderRadius: "6px",
                    backgroundColor: activeIndex === idx ? (primaryColor || BUTTON_COLOR) : (primaryColor || BUTTON_COLOR) + "33",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </div>
          )}

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              padding: activeOrders.length > 1 ? "4px 0 12px 0" : "12px 0",
            }}
          >
            <style>
              {`::-webkit-scrollbar { display: none; }`}
            </style>
            
            {activeOrders.map((order, idx) => {
              const orderStoreInfo = order?.store_details || {};
              const logo = orderStoreInfo.store_logo || storeInfo?.store_display_logo || defaultimg;
              const storeName = orderStoreInfo.store_name || storeInfo?.store_name || "Store";
              
              return (
                <div
                  key={idx}
                  onClick={() => navigate("/customer")}
                  style={{
                    flex: "0 0 100%",
                    scrollSnapAlign: "center",
                    padding: "0 16px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={logo}
                    alt="store"
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                    onError={(e) => { e.target.onerror = null; e.target.src = defaultimg; }}
                  />
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography
                      style={{
                        fontFamily: "Satoshi",
                        fontSize: "14px",
                        fontWeight: "700",
                        color: "#1a1a1a",
                        lineHeight: "1.2",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "left",
                      }}
                    >
                      {storeName}
                    </Typography>
                    <Typography
                      style={{
                        fontFamily: "Satoshi",
                        fontSize: "12px",
                        color: "#666",
                        lineHeight: "1.2",
                        marginTop: "2px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "left",
                        maxWidth:"15rem"
                      }}
                    >
                      {order?.item_details?.[0]?.item_name || "Items"} {order?.item_details?.length > 1 ? `+${order.item_details.length - 1} more` : ""}
                    </Typography>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: "70px", flexShrink: 0 }}>
                    <Typography
                      style={{
                        fontFamily: "Satoshi",
                        fontSize: "14px",
                        fontWeight: "700",
                        color: "#1a1a1a",
                      }}
                    >
                       {currency_code} {order?.total_amount}
                      {/* {currency_code} {order?.transaction_grand_amount || order?.rounded_total || order?.total_gross_sale || order?.total_amount || ""} */}
                    </Typography>
                      <div
                        style={{
                          backgroundColor: primaryColor || BUTTON_COLOR,
                          color: "#ffffff",
                          borderRadius: "8px",
                          padding: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginTop: "6px",
                          gap: "6px",
                          boxSizing: "border-box",
                        }}
                      >
                        {(order?.order_state || order?.order_status || "").toUpperCase() === "READY" ? (
                          <>
                            <div
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: "white",
                              }}
                            />
                            <Typography
                              style={{
                                fontFamily: "Satoshi",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#ffffff",
                                textTransform: "none",
                              }}
                            >
                              Ready
                            </Typography>
                          </>
                        ) : (order?.order_state || order?.order_status || "").toUpperCase() === "PLACED" ? (
                          <>
                            <img
                              className="preparing-blink"
                              src={require("../assets/images/Preparing.svg").default || require("../assets/images/Preparing.svg")}
                              style={{ width: "12px", height: "12px" }}
                              alt=""
                            />
                            <Typography
                              style={{
                                fontFamily: "Satoshi",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#ffffff",
                                textTransform: "none",
                              }}
                            >
                              Placed
                            </Typography>
                          </>
                        ) : (
                          <>
                            <img
                              className="preparing-blink"
                              src={require("../assets/images/Preparing.svg").default || require("../assets/images/Preparing.svg")}
                              style={{ width: "12px", height: "12px" }}
                              alt=""
                            />
                            <Typography
                              style={{
                                fontFamily: "Satoshi",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#ffffff",
                                textTransform: "none",
                              }}
                            >
                              Preparing
                            </Typography>
                          </>
                        )}
                      </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  reducer: state.customerReducer,
  cartProps: state.cartReducer,
});

export default connect(mapStateToProps, { set_last_order })(ActiveOrderTray);